import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNotification } from '../../contexts/NotificationContext';

interface Props {
  defaultWorkspaceId?: string;
}

const AdminEcommerceEventTester: React.FC<Props> = ({ defaultWorkspaceId }) => {
  const { showSuccess, showError } = useNotification();
  const [workspaceId, setWorkspaceId] = useState<string>(defaultWorkspaceId || '');
  const [contactId, setContactId] = useState<string>('');
  const [eventType, setEventType] = useState<'added_to_cart' | 'started_checkout' | 'abandoned_cart' | 'placed_order' | 'product_viewed'>('added_to_cart');
  const [price, setPrice] = useState<number>(1299);
  const [category, setCategory] = useState<string>('Electronics');
  const [loading, setLoading] = useState(false);

  const buildPayload = () => {
    switch (eventType) {
      case 'added_to_cart':
        return {
          addedItem: {
            productID: 'TEST-PROD',
            productTitle: 'Test Product',
            productPrice: price,
            productSKU: 'TEST-SKU',
            productCategories: [{ id: 'TEST-CAT', title: category }],
            productQuantity: 1
          },
          cart: {
            id: 'TEST-CART',
            value: price,
            currency: 'USD',
            itemCount: 1,
            lineItems: [
              { id: 'TEST-PROD', title: 'Test Product', price, quantity: 1, sku: 'TEST-SKU' }
            ]
          },
          abandonedCheckoutURL: 'https://example.com/checkout?cart=TEST-CART'
        };
      case 'started_checkout':
        return {
          cart: {
            id: 'TEST-CART',
            value: price,
            currency: 'USD',
            itemCount: 2,
            lineItems: [
              { id: 'TEST-A', title: 'Item A', price: Math.max(1, Math.floor(price / 2)), quantity: 1, sku: 'A' },
              { id: 'TEST-B', title: 'Item B', price: Math.max(1, price - Math.floor(price / 2)), quantity: 1, sku: 'B' }
            ]
          },
          abandonedCheckoutURL: 'https://example.com/checkout?cart=TEST-CART'
        };
      case 'abandoned_cart':
        return {
          cart: {
            id: 'TEST-CART',
            value: price,
            currency: 'USD',
            itemCount: 1,
            lineItems: [
              { id: 'TEST-PROD', title: 'Test Product', price, quantity: 1, sku: 'TEST-SKU' }
            ]
          },
          abandonedCheckoutURL: 'https://example.com/checkout?cart=TEST-CART'
        };
      case 'placed_order':
        return {
          orderId: 'ORDER-TEST',
          orderTotal: price,
          orderCurrency: 'USD',
          lineItems: [
            { id: 'TEST-PROD', title: 'Test Product', price, quantity: 1, sku: 'TEST-SKU' }
          ]
        };
      case 'product_viewed':
        return {
          product: {
            id: 'TEST-PROD',
            title: 'Test Product',
            price,
            sku: 'TEST-SKU',
            categories: [{ id: 'TEST-CAT', title: category }]
          }
        };
      default:
        return {};
    }
  };

  const handleInsert = async () => {
    if (!workspaceId || !contactId) {
      showError('Please provide both workspaceId and contactId');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'ecommerce_events'), {
        workspaceId,
        contactId,
        eventType,
        processed: false,
        timestamp: serverTimestamp(),
        ...buildPayload()
      });
      showSuccess('Inserted test e-commerce event');
    } catch (err) {
      console.error(err);
      showError('Failed to insert event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-neutral-700 rounded-lg p-4 bg-white dark:bg-neutral-800">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Insert test e-commerce event</h3>
        <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Admin</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <input
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          placeholder="Workspace ID"
          className="px-3 py-2 border rounded-md bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-sm"
        />
        <input
          value={contactId}
          onChange={(e) => setContactId(e.target.value)}
          placeholder="Contact ID"
          className="px-3 py-2 border rounded-md bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-sm"
        />
        <select
          value={eventType}
          onChange={(e) => setEventType(e.target.value as any)}
          className="px-3 py-2 border rounded-md bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-sm"
        >
          <option value="added_to_cart">Added to cart</option>
          <option value="started_checkout">Started checkout</option>
          <option value="abandoned_cart">Abandoned cart</option>
          <option value="placed_order">Placed order</option>
          <option value="product_viewed">Product viewed</option>
        </select>
        <div className="flex items-center space-x-2">
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            placeholder="Price"
            className="px-3 py-2 border rounded-md w-full bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-sm"
          />
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Category"
            className="px-3 py-2 border rounded-md w-full bg-white dark:bg-neutral-900 border-gray-300 dark:border-neutral-700 text-sm"
          />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button
          onClick={handleInsert}
          disabled={loading}
          className="px-4 py-2 rounded-md bg-black text-white hover:bg-gray-800 disabled:opacity-50 text-sm"
        >
          {loading ? 'Inserting…' : 'Insert test event'}
        </button>
      </div>
    </div>
  );
};

export default AdminEcommerceEventTester;






























